import { useEffect, useState } from "react";
import { Bar } from "@nivo/bar";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Avatar from "@mui/material/Avatar";
import { useQueryTrend } from "../../hooks/top18ApiHooks";
import { useWindowSize } from "../../hooks/windowHooks";
import { useAppStore } from "../../store/appStore";
import { sidebarWidth } from "../Menu";
import Top18TrendLoading from "./Top18TrendLoading";
import { pad } from "../../fn/pad";

const TrendPage = () => {
	const { width, height } = useWindowSize();
	const darkModeEnabled = useAppStore((state) => state.darkModeEnabled);
	const listOption = useAppStore((state) => state.listOption);

	const top18TrendsRangeStart = useAppStore(
		(state) => state.top18TrendsRangeStart
	);
	const top18TrendsRangeEnd = useAppStore((state) => state.top18TrendsRangeEnd);
	const top18TrendsInterval = useAppStore((state) => state.top18TrendsInterval);
	const top18TimeFilter = useAppStore((state) => state.top18TimeFilter);

	const { isLoading, isError, error, data } = useQueryTrend({
		listOption,
		timeRangeMin: top18TrendsRangeStart,
		timeRangeMax: top18TrendsRangeEnd,
		timeInterval: top18TrendsInterval,
		timeFilter: top18TimeFilter,
	});

	const [localData, setLocalData] = useState([]);

	useEffect(() => {
		if (data) {
			const chartData = data.voteTrends;

			setLocalData([...chartData]);
		}
	}, [data]);

	if (isLoading || !width || !height) return <Top18TrendLoading />;

	if (isError) return <div>Error found, {error}</div>;

	const ChartTooltip = ({ id, color, index, indexValue }) => {
		const originalData = data.voteTrendsOriginal;
		if (!(index in originalData) || !(id in originalData[index])) {
			return <Box />;
		}

		const StyledBadge = styled(Badge)(({ theme }) => ({
			"& .MuiBadge-badge": {
				backgroundColor: color,
				color,
				boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
				"&::after": {
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					borderRadius: "50%",
					animation: "ripple 1.2s infinite ease-in-out",
					border: "1px solid currentColor",
					content: '""',
				},
			},
			"@keyframes ripple": {
				"0%": {
					transform: "scale(.8)",
					opacity: 1,
				},
				"100%": {
					transform: "scale(2.4)",
					opacity: 0,
				},
			},
		}));

		return (
			<Paper>
				<List dense disablePadding>
					<ListSubheader>
						<Stack direction="row" alignItems="center" spacing={2}>
							<ListItemIcon>
								<StyledBadge
									overlap="circular"
									anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
									variant="dot"
								>
									<Avatar
										alt={`${pad(id, 2)}`}
										src={`${process.env.PUBLIC_URL}/images/${
											listOption === "f" ? "f" : "m"
										}/top21/${parseInt(id.substring(1))}.jpg`}
										sx={{ width: 64, height: 64 }}
									/>
								</StyledBadge>
							</ListItemIcon>
							<Typography>{pad(id, 2)}</Typography>
						</Stack>

						<ListItemSecondaryAction>
							<Typography>{listOption === "f" ? "🌸" : "🍀"}</Typography>
						</ListItemSecondaryAction>
					</ListSubheader>
					<ListItem
						secondaryAction={
							<Typography>{`${originalData[index][id]}`}%</Typography>
						}
					>
						<ListItemText primary={indexValue} sx={{ minWidth: 150 }} />
					</ListItem>
				</List>
			</Paper>
		);
	};

	const chartProps = {
		data: localData,
		height: height - 20,
		width:
			localData.length * 150 > (width >= 1080 ? width - sidebarWidth : width)
				? localData.length * 150
				: width >= 1080
				? width - sidebarWidth
				: width,
		keys: data.records.map(({ id }) => `#${pad(id, 2)}`),
		indexBy: "time",
		margin: ["bottom", "left", "right", "top"].reduce(
			(acc, key) => ({ ...acc, [key]: 40 }),
			{}
		),
		padding: 0.3,
		valueScale: { type: "linear" },
		indexScale: { type: "band", round: true },
		theme: {
			textColor: darkModeEnabled ? "#FFF" : "#000",
			legends: { title: { text: darkModeEnabled ? "#FFF" : "#000" } },
		},
		label: (d) => `${d.id} (${d.value}%)`,
		colors: { scheme: darkModeEnabled ? "dark2" : "paired" },
		tooltip: ChartTooltip,
		axisTop: null,
		axisRight: null,
		axisBottom: {
			tickSize: 5,
			tickPadding: 5,
			tickRotation: 0,
			legend: "",
			legendPosition: "middle",
			legendOffset: 32,
		},
		axisLeft: {
			tickSize: 5,
			tickPadding: 5,
			tickRotation: 0,
			legend: "票數 %",
			legendPosition: "middle",
			legendOffset: -40,
		},
		animate: false,
	};

	return (
		<Box
			width={width >= 1080 ? width - sidebarWidth : width}
			height={height}
			display="flex"
		>
			<Bar
				{...chartProps}
				layers={["axes"]}
				axisBottom={null}
				width={50}
				margin={{ ...chartProps.margin, left: 50 }}
			/>
			<Box
				sx={{ overflowX: "scroll", overflowY: "hidden" }}
				width={width >= 1080 ? width - sidebarWidth : width}
				height={height - 10}
			>
				<Bar
					{...chartProps}
					axisLeft={null}
					margin={{ ...chartProps.margin, left: 0 }}
				/>
			</Box>
		</Box>
	);
};

export default TrendPage;
